import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import SendIcon from "@mui/icons-material/Send";
import SummarizeIcon from "@mui/icons-material/Summarize";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Snackbar,
  Tooltip
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface StreamingAvatarTextInputProps {
  label: string;
  placeholder?: string;
  input: string;
  onSubmit: () => void;
  handleOpenSummary: () => void;
  setInput: (value: string) => void;
  endContent?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  onRecord: (blob: Blob) => void;
  audioBlob?: Blob | null;
  onDeleteAudio: () => void;
}

export const InteractiveAvatarTextInput = ({
  label,
  placeholder,
  input,
  onSubmit,
  handleOpenSummary,
  setInput,
  endContent,
  disabled = false,
  loading = false,
  onRecord,
  audioBlob,
  onDeleteAudio
}: StreamingAvatarTextInputProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [isRecording, setIsRecording] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const recorderRef = useRef<InstanceType<typeof MicRecorder> | null>(null);
  const MicRecorder = require("mic-recorder-to-mp3");

  const handleSubmit = () => {
    if (input.trim() === "" && !audioBlob) {
      return;
    }
    onSubmit();
    setInput("");
  };

  const startRecording = async () => {
    if (audioBlob) {
      onDeleteAudio();
    }

    if (!recorderRef.current) {
      recorderRef.current = new MicRecorder({ bitRate: 565 });
    }

    try {
      await recorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Error starting recording:", err);
      setConnectionError(t("chatbot.errors.mic_access_error"));
    }
  };

  const stopRecording = async () => {
    if (recorderRef.current && isRecording) {
      try {
        const [, blob] = await recorderRef.current.stop().getMp3();
        onRecord(blob);
        setIsRecording(false);
        setTimeout(() => handleSubmit(), 100);
      } catch (err) {
        console.error("Error stoping recording:", err);
        setIsRecording(false);
        setConnectionError(t("chatbot.errors.recording_error"));
      }
    }
  };

  useEffect(() => {
    return () => {
      if (isRecording && recorderRef.current) {
        recorderRef.current.stop();
      }
    };
  }, [isRecording]);

  useEffect(() => {
    if (audioBlob && input.trim() === "") {
      handleSubmit();
    }
  }, [input, audioBlob]);

  const RecordButton = () => (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Tooltip
        title={
          !isRecording
            ? t("chatbot.actions.start_recording")
            : t("chatbot.actions.recording")
        }
      >
        <Box
          sx={{
            transition: "transform 0.2s ease-in-out",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center"
          }}
        >
          <IconButton
            size={"small"}
            onMouseDown={startRecording}
            onMouseUp={stopRecording}
            onMouseLeave={isRecording ? stopRecording : undefined}
            onTouchStart={startRecording}
            onTouchEnd={stopRecording}
            disabled={disabled || loading}
            sx={{
              backgroundColor: isRecording ? "red" : "#fff",
              border: "1px solid #eee",
              "&:hover": {
                backgroundColor: isRecording ? "red" : "#f9f9f9"
              }
            }}
          >
            {isRecording ? <MicOffIcon sx={{ color: "white" }} /> : <MicIcon />}
          </IconButton>
        </Box>
      </Tooltip>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {connectionError && (
        <Snackbar
          open={!!connectionError}
          autoHideDuration={6000}
          onClose={() => setConnectionError(null)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setConnectionError(null)}
            severity="error"
            sx={{ width: "100%" }}
          >
            {connectionError}
          </Alert>
        </Snackbar>
      )}

      <Box
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          borderColor: disabled
            ? theme.palette.customColors.disabledColor
            : "#979797",
          flexDirection: "column",
          borderRadius: 3,
          p: 2,
          m: 1,
          bgcolor: disabled ? "gray" : "white",
          "&:focus-within": {
            borderColor: "indigo.500"
          },
          border: "1px solid #d8d8d8",
          boxShadow: "0 3px 8px rgba(0, 0, 0, 0.15)"
        }}
      >
        <Box sx={{ display: "flex", width: "100%", alignItems: "center" }}>
          <RecordButton />

          <InputBase
            fullWidth
            placeholder={placeholder}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !loading) {
                handleSubmit();
              }
            }}
            disabled={disabled || loading || isRecording || Boolean(audioBlob)}
            sx={{ flex: 1, pl: 1, fontSize: { xs: 12, sm: 15 } }}
            inputProps={{ "aria-label": label }}
          />

          <Box display="flex" alignItems="center" gap={0.5}>
            {endContent && (
              <Box component="span" color="gray.500">
                {endContent}
              </Box>
            )}
            <span>
              {loading && (
                <CircularProgress
                  size={20}
                  sx={{ color: "indigo.500" }}
                  aria-label="Loading"
                />
              )}
            </span>
            <Tooltip
              title={t("chatbot.actions.send_message")}
              aria-label={t("chatbot.actions.send_message")}
            >
              <span>
                <IconButton
                  onClick={handleSubmit}
                  disabled={disabled || loading}
                >
                  <SendIcon
                    sx={{ fontSize: { xs: 18, sm: 23 } }}
                    color="primary"
                    className={disabled || loading ? "opacity-50" : ""}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={t("chatbot.actions.generate_summary")}
              aria-label={t("chatbot.actions.generate_summary")}
            >
              <IconButton
                onClick={handleOpenSummary}
                disabled={disabled || loading}
              >
                <SummarizeIcon
                  sx={{
                    fontSize: { xs: 18, sm: 23 }
                  }}
                  color="primary"
                  className={disabled || loading ? "opacity-50" : ""}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
